import React from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  GenerationStatusBadge,
  ScheduleStatusBadge,
} from './StatusBadge';
import {
  buildScheduledMessagesEditPath,
  contactLabel,
  formatDateTime,
  formatGenerationMode,
  formatScheduleType,
  formatTimezoneLabel,
} from '../utils';
import { buildScheduledMessageLoadingState } from '../scheduledMessageLoadingState';
import ScheduleSummaryDisplay from './ScheduleSummaryDisplay';
import ScheduledMessageDetailActions from './ScheduledMessageDetailActions';

function OverviewField({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary mt-1 break-all">{children}</dd>
    </div>
  );
}

function ScheduledMessageAdminDetail({
  item,
  mode,
  agentId,
  listPath,
  downloadableAttachments,
  cancelError,
  deleteError,
  retryError,
  resendError,
  dispatchError,
  sendTemplateError,
  cancelling,
  deleting,
  retrying,
  resending,
  dispatching,
  sendingTemplate,
  onCancel,
  onDelete,
  onRetry,
  onResend,
  onForceDispatch,
  onSendTemplate,
  onBack,
}) {
  const loadingState = buildScheduledMessageLoadingState({
    itemId: item.id,
    cancelling,
    deleting,
    retrying,
    resending,
    dispatching,
    sendingTemplate,
  });
  const editTo = buildScheduledMessagesEditPath(mode, {
    agentId: agentId || item.agent_id,
    scheduledMessageId: item.id,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to={listPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
            ← Voltar aos agendamentos
          </Link>
          <h1 className="text-2xl font-bold text-content-primary mt-2">Detalhe do agendamento</h1>
          <p className="text-content-muted text-sm mt-1">
            {contactLabel(item)} · {item.template_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScheduleStatusBadge status={item.status} />
          {item.content_generation_mode === 'batch' && (
            <GenerationStatusBadge status={item.generation_status} />
          )}
        </div>
      </div>

      {cancelError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{cancelError}</p>
        </div>
      )}

      {deleteError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{deleteError}</p>
        </div>
      )}

      {retryError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{retryError}</p>
        </div>
      )}

      {resendError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{resendError}</p>
        </div>
      )}

      {dispatchError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{dispatchError}</p>
        </div>
      )}
      {sendTemplateError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{sendTemplateError}</p>
        </div>
      )}

      <section className="surface-card p-4">
        <h2 className="text-sm font-semibold text-content-primary mb-4">Resumo</h2>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <OverviewField label="Agente">{item.agent?.name || '—'}</OverviewField>
          <OverviewField label="Conta">
            {[item.account?.name, item.account?.email].filter(Boolean).join(' · ') || '—'}
          </OverviewField>
          <OverviewField label="Contato">{contactLabel(item)}</OverviewField>
          <OverviewField label="Template">{item.template_name}</OverviewField>
          <OverviewField label="Instrução para geração">
            <span className="whitespace-pre-wrap">{item.generation_instruction || '—'}</span>
          </OverviewField>
          <OverviewField label="Modo de geração">
            {formatGenerationMode(item.content_generation_mode)}
          </OverviewField>
          {item.content_generation_mode === 'batch' && (
            <OverviewField label="Antecedência (dias)">{item.batch_lead_days}</OverviewField>
          )}
          <OverviewField label="Tipo">{formatScheduleType(item.schedule_type)}</OverviewField>
          <OverviewField label="Agenda">
            <ScheduleSummaryDisplay item={item} />
          </OverviewField>
          <OverviewField label="Fuso horário">{formatTimezoneLabel(item.timezone)}</OverviewField>
          {item.ends_at && (
            <OverviewField label="Término">{formatDateTime(item.ends_at)}</OverviewField>
          )}
          <OverviewField label="Próximo envio">{formatDateTime(item.next_run_at)}</OverviewField>
          <OverviewField label="Gerar em">{formatDateTime(item.generate_at)}</OverviewField>
          <OverviewField label="Último envio">{formatDateTime(item.last_sent_at)}</OverviewField>
          <OverviewField label="Envios realizados">{item.sent_count ?? 0}</OverviewField>
          <OverviewField label="Criado em">{formatDateTime(item.created_at)}</OverviewField>
          <OverviewField label="Atualizado em">{formatDateTime(item.updated_at)}</OverviewField>
        </dl>
      </section>

      {item.last_error && (
        <section className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h2 className="text-sm font-semibold text-red-800 mb-2">Último erro</h2>
          <p className="text-sm text-red-700 whitespace-pre-wrap">{item.last_error}</p>
        </section>
      )}

      {item.last_dispatch_result && (
        <section className="surface-card p-4">
          <h2 className="text-sm font-semibold text-content-primary mb-2">Último resultado de envio</h2>
          <pre className="text-xs text-content-secondary overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(item.last_dispatch_result, null, 2)}
          </pre>
        </section>
      )}

      {item.generated_content && (
        <section className="surface-card p-4">
          <h2 className="text-sm font-semibold text-content-primary mb-2">Conteúdo gerado</h2>
          <p className="text-xs text-content-muted mb-3">
            Gerado em {formatDateTime(item.generated_at)}
          </p>
          <div className="rounded-lg border border-surface-border bg-slate-50 p-3 text-sm text-content-primary whitespace-pre-wrap">
            {item.generated_content}
          </div>
        </section>
      )}

      {downloadableAttachments.length > 0 && (
        <section className="surface-card p-4">
          <h2 className="text-sm font-semibold text-content-primary mb-3">Arquivos gerados</h2>
          <div className="flex flex-wrap gap-2">
            {downloadableAttachments.map((attachment, index) => {
              const label = attachment.file_name
                ? `Baixar ${attachment.file_name}`
                : 'Baixar arquivo';
              return (
                <a
                  key={`${attachment.download_url}-${index}`}
                  href={attachment.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={attachment.file_name || undefined}
                  className="inline-flex items-center gap-2 btn-secondary text-sm px-4 py-2"
                >
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                  {label}
                </a>
              );
            })}
          </div>
        </section>
      )}

      <ScheduledMessageDetailActions
        item={item}
        editTo={editTo}
        loadingState={loadingState}
        onCancel={onCancel}
        onDelete={onDelete}
        onRetry={onRetry}
        onResend={onResend}
        onForceDispatch={onForceDispatch}
        onSendTemplate={onSendTemplate}
        onBack={onBack}
      />
    </div>
  );
}

export default ScheduledMessageAdminDetail;
